// Offer Feedback Page - For customers to share rejection reasons
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  MessageSquare,
  Loader2,
  CheckCircle,
  XCircle,
  DollarSign,
  Search,
  Clock,
  Package,
  HelpCircle,
  Ship
} from "lucide-react";


const REASON_ICONS = {
  price_high: DollarSign,
  found_other: Search,
  no_longer_needed: XCircle,
  timing_not_right: Clock,
  service_not_match: Package,
  other: HelpCircle
};

export default function OfferFeedbackPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    fetchOfferData();
  }, [token]);

  const fetchOfferData = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/offer-response/${token}`);
      setOfferData(response.data);
    } catch (err) {
      setError("This feedback link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error("Please select a reason");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/public/offer-feedback/${token}`, {
        reason: selectedReason,
        other_text: selectedReason === "other" ? otherText : null
      });
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
      
      // Redirect to survey after a short delay
      setTimeout(() => {
        navigate(`/offer-survey/${token}`);
      }, 2000);
    } catch (err) {
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center"
        >
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600">Your feedback helps us improve. Redirecting to a quick survey...</p>
        </motion.div>
      </div>
    );
  }

  const { rejection_reasons, followup } = offerData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-[#1e3a5f] text-white px-6 py-3 rounded-full mb-6">
          <Ship className="h-5 w-5" />
          <span className="font-semibold">Europacific</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Help Us Improve</h1>
        <p className="text-gray-600">
          We'd appreciate your feedback on quote <strong>{followup?.offer_number}</strong>
        </p>
      </div>

      {/* Feedback Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="h-6 w-6 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">
              What was the main reason for declining?
            </h2>
          </div>

          <div className="grid gap-3 mb-6">
            {rejection_reasons?.map((reason) => {
              const Icon = REASON_ICONS[reason.code] || HelpCircle;
              const isSelected = selectedReason === reason.code;
              
              return (
                <button
                  key={reason.code}
                  onClick={() => setSelectedReason(reason.code)}
                  className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  data-testid={`reason-${reason.code}`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className={`font-medium ${isSelected ? "text-primary" : "text-gray-700"}`}>
                    {reason.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="h-5 w-5 text-primary ml-auto" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Other reason text input */}
          {selectedReason === "other" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-6"
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Please tell us more (optional)
              </label>
              <Textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Share any additional details..."
                className="min-h-[100px]"
                data-testid="other-reason-text"
              />
            </motion.div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedReason}
            className="w-full h-12 bg-primary hover:bg-primary/90"
            data-testid="submit-feedback-btn"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "Submit Feedback"
            )}
          </Button>
        </div>
      </motion.div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto mt-8 text-center text-gray-500 text-sm">
        <p>Your feedback is confidential and helps us serve you better.</p>
        <p className="mt-2">© 2026 Europacific d.o.o. | Logistics Simplified</p>
      </div>
    </div>
  );
}
